/**
 * Hero do canal: modo clássico (Nexus + camadas) ou modo legado HubSpot (banner),
 * sem escolha de público — tipo_publico é apenas no formulário.
 */
import React, { useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, Shield, Lock, Scale } from 'lucide-react';
import { FIUS_PARTNER_LOGO_DATA_URL } from '../constants/fiusPartnerLogoDataUrl';
import { FIUS_LOGO_NEGATIVO_BRANCO_URL, resolvePortalBannerUrl } from '../constants/portalLandingAssets';
import NexusParticleField from './NexusParticleField';
import ComplaintPortalNavBar from './ComplaintPortalNavBar';

const easeNexus = [0.16, 1, 0.3, 1];

const fontNexus = "'Montserrat', system-ui, sans-serif";

const fontLegacyBody = "'Verdana', 'Geneva', sans-serif";

function CommitmentCopy({ clientName, variant }) {
  const bodyFont = variant === 'daterra' ? fontLegacyBody : fontNexus;
  return (
    <div
      className="mx-auto max-w-3xl space-y-5 text-center text-[0.95rem] leading-relaxed text-[rgba(17,29,48,0.82)] md:text-left"
      style={{ fontFamily: bodyFont }}
    >
      <p className="mb-0">
        A <strong className="font-semibold text-[#111d30]">{clientName}</strong> adota comportamento
        íntegro e transparente em suas relações internas e externas, e abre espaço para que
        colaboradores e terceiros possam reportar suspeitas de corrupção, fraude, práticas abusivas,
        discriminação, assédio ou outras condutas contrárias aos nossos princípios.
      </p>
      <p className="mb-0">
        Este canal é seguro, confidencial e operado por terceiro, permitindo que qualquer pessoa
        reporte irregularidades sem medo de represálias. As denúncias podem ser feitas de forma
        anônima e serão analisadas de maneira imparcial.
      </p>
    </div>
  );
}

/** Rodapé escuro como em report.fius.com.br/daterra (logo FIUS branco). */
function DaterraDarkFooter({ clientName }) {
  return (
    <div className="mt-14 w-full border-t border-white/10 bg-[#111d30] py-12">
      <div className="mx-auto max-w-5xl px-4 text-center">
        <p
          className="mb-8 text-[0.95rem] leading-snug text-white/95"
          style={{ fontFamily: fontLegacyBody }}
        >
          Canal de Denúncias · {clientName}
        </p>
        <img
          src={FIUS_LOGO_NEGATIVO_BRANCO_URL}
          alt="FIUS — Finocchio e Ustra"
          className="mx-auto h-9 object-contain opacity-95 md:h-10"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );
}

const ComplaintPortalHero = ({ clientSlug, form, onNavDenunciar, onScrollToForm }) => {
  const sectionRef = useRef(null);
  const reducedMotion = useReducedMotion();

  const bannerUrl = resolvePortalBannerUrl(
    clientSlug,
    process.env.REACT_APP_PORTAL_BANNER_URL,
    form?.portal_banner_url
  );
  const hubspotStyle = Boolean(bannerUrl);

  const trustStrip = (
    <ul className="mb-10 grid gap-3 sm:grid-cols-3">
      {[
        {
          Icon: Lock,
          title: 'Privacidade',
          text: 'Protocolo seguro e tratamento restrito das informações.',
        },
        {
          Icon: Scale,
          title: 'Independência',
          text: 'Operação por escritório terceiro, sem vínculo com a sua chefia.',
        },
        {
          Icon: Shield,
          title: 'Sem retaliação',
          text: 'Denúncia pode ser anônima; análise apartada das áreas envolvidas.',
        },
      ].map(({ Icon, title, text }) => (
        <li
          key={title}
          className="flex gap-3 rounded-2xl border border-black/[0.06] bg-white/90 p-4 text-left shadow-[0_8px_28px_rgba(0,0,0,0.05)]"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[rgba(0,158,219,0.1)] text-[#009edb]">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <span>
            <span
              className="block text-sm font-semibold text-[#111d30]"
              style={{ fontFamily: fontNexus }}
            >
              {title}
            </span>
            <span className="mt-1 block text-[0.8rem] leading-snug text-[rgba(17,29,48,0.65)]">
              {text}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );

  const landingFooter = (
    <footer className="mt-10 flex flex-col items-center gap-4 border-t border-black/[0.06] pt-8 text-center md:flex-row md:justify-between md:text-left">
      <div className="flex flex-col items-center gap-2 md:flex-row md:items-center md:gap-4">
        <img
          src={FIUS_PARTNER_LOGO_DATA_URL}
          alt="Finocchio e Ustra Advogados"
          className="h-9 object-contain opacity-90"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <p
          className="max-w-md text-[0.8rem] leading-relaxed text-[rgba(17,29,48,0.55)]"
          style={{ fontFamily: fontNexus }}
        >
          Infraestrutura e suporte jurídico do canal sob responsabilidade de Finocchio e Ustra —
          Sociedade de Advogados, em papel independente da gestão da empresa.
        </p>
      </div>
      <p
        className="text-[0.65rem] uppercase tracking-[0.16em] text-[rgba(17,29,48,0.35)]"
        style={{ fontFamily: fontNexus }}
      >
        FIUS · canal de denúncias
      </p>
    </footer>
  );

  /**
   * Modo banner (Daterra): uma secção única no formato das caixas antigas do HubSpot —
   * título forte + uma linha de texto + Denunciar (sem blocos por público).
   */
  const primaryTaskDaterra = (
    <article
      id="hero-primary-task"
      className="scroll-mt-[5.5rem] mt-14 border-b border-black/[0.14] pb-14"
    >
      <h2
        className="mb-5 text-[clamp(1.35rem,3.8vw,2rem)] font-bold leading-tight text-[#111d30]"
        style={{ fontFamily: fontLegacyBody }}
      >
        Denúncia
      </h2>
      <p
        className="mb-8 max-w-3xl text-[0.95rem] leading-relaxed text-[rgba(17,29,48,0.88)] md:text-[1rem]"
        style={{ fontFamily: fontLegacyBody }}
      >
        Se você deseja fazer uma denúncia, clique no botão abaixo. O formulário seguro orientará você
        nas etapas seguintes.
      </p>
      <motion.button
        type="button"
        onClick={onScrollToForm}
        className="inline-flex items-center justify-center gap-2 rounded px-8 py-3.5 text-[0.95rem] font-bold text-white shadow-[0_10px_28px_rgba(0,158,219,.35)]"
        style={{
          fontFamily: fontLegacyBody,
          background: '#009edb',
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.99 }}
      >
        Denunciar
        <ChevronRight className="h-4 w-4" aria-hidden />
      </motion.button>
    </article>
  );

  const primaryTaskBlock = hubspotStyle ? (
    primaryTaskDaterra
  ) : (
    <div
      id="hero-primary-task"
      className="scroll-mt-[5.5rem] rounded-[24px] border border-[rgba(0,158,219,0.18)] bg-[linear-gradient(180deg,rgba(0,158,219,0.06)_0%,rgba(255,255,255,0.96)_100%)] p-6 shadow-[0_20px_50px_rgba(17,29,48,0.07)] md:p-8"
    >
      <p
        className="mb-1 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-[#009edb]"
        style={{ fontFamily: fontNexus }}
      >
        Próximo passo
      </p>
      <h2
        className="mb-3 text-xl font-semibold text-[#111d30] md:text-2xl"
        style={{ fontFamily: fontNexus }}
      >
        Conte o que ocorreu
      </h2>
      <p className="mb-6 max-w-xl text-sm text-[rgba(17,29,48,0.62)]">
        O formulário guia cada etapa do relato de forma confidencial.
      </p>
      <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
        <motion.button
          type="button"
          onClick={onScrollToForm}
          className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white shadow-[0_14px_40px_rgba(0,158,219,.36)]"
          style={{ fontFamily: fontNexus, background: '#009edb' }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          Abrir formulário
          <ChevronRight className="h-5 w-5" aria-hidden />
        </motion.button>
        <p className="text-center text-sm text-[rgba(17,29,48,0.55)] sm:text-left">
          Relatos sobre corrupção, assédio, fraude e outras condutas previstas na política da
          empresa.
        </p>
      </div>
    </div>
  );

  return (
    <>
      <ComplaintPortalNavBar
        clientName={form.client_name}
        companyLogo={form.company_logo}
        onDenunciar={onNavDenunciar}
      />

      <div className="relative pt-[4.25rem]">
        <motion.section
          ref={sectionRef}
          id="complaint-portal-hero"
          className={
            hubspotStyle
              ? 'relative overflow-hidden border-b border-black/[0.06] bg-[#f3f5f9]'
              : 'relative overflow-hidden rounded-b-[24px] border-b border-black/[0.06] bg-white'
          }
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, ease: easeNexus }}
        >
          {!hubspotStyle && reducedMotion ? (
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(0,158,219,.06) 0%, transparent 52%), radial-gradient(ellipse 120% 120% at 50% 100%, rgba(0,158,219,.12), transparent 62%)',
              }}
              aria-hidden
            />
          ) : null}
          {!hubspotStyle && !reducedMotion ? <NexusParticleField /> : null}

          {!hubspotStyle ? (
            <div
              className="pointer-events-none absolute inset-0 z-[1]"
              style={{
                background:
                  'radial-gradient(ellipse 120% 120% at 50% 120%, rgba(0,158,219,.12), transparent 58%), radial-gradient(circle at 100% 0%, rgba(17,29,48,.05), transparent 50%)',
              }}
              aria-hidden
            />
          ) : null}

          {hubspotStyle ? (
            <div className="relative z-10 w-full">
              <div className="flex justify-center bg-[#111d30] py-3.5">
                <img
                  src={FIUS_LOGO_NEGATIVO_BRANCO_URL}
                  alt="FIUS — Finocchio e Ustra"
                  className="h-6 object-contain md:h-7"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="bg-[#e8ecf2]">
                <img
                  src={bannerUrl}
                  alt={`Canal de denúncias · ${form.client_name}`}
                  className="mx-auto max-h-[min(520px,58vh)] w-full object-cover object-center"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          ) : null}

          <div className="relative z-10 mx-auto max-w-5xl px-4 pb-12 pt-8 md:pb-16 md:pt-10">
            {!hubspotStyle ? (
              <>
                <header className="mb-8 text-center md:mb-10">
                  <p
                    className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[rgba(17,29,48,0.5)]"
                    style={{ fontFamily: fontNexus }}
                  >
                    Canal de denúncias
                  </p>
                  <h1
                    className="mx-auto max-w-3xl text-balance text-[clamp(1.6rem,4.2vw,2.5rem)] font-bold leading-[1.15] tracking-[0.02em] text-[#111d30]"
                    style={{ fontFamily: fontNexus }}
                  >
                    {form.client_name}
                  </h1>
                  <p
                    className="mx-auto mt-3 max-w-xl text-[0.95rem] leading-relaxed text-[rgba(17,29,48,0.72)]"
                    style={{ fontFamily: fontNexus }}
                  >
                    Espaço para relatar irregularidades com sigilo. Este canal não substitui canais
                    internos da empresa — complementa-os com garantias de confidencialidade e gestão
                    por terceiro.
                  </p>
                </header>

                {trustStrip}

                {primaryTaskBlock}

                <details className="group mt-10 rounded-2xl border border-black/[0.06] bg-white/80 px-5 py-4 text-left shadow-[0_8px_28px_rgba(0,0,0,0.04)]">
                  <summary
                    className="cursor-pointer list-none text-sm font-semibold text-[#111d30] outline-none [&::-webkit-details-marker]:hidden"
                    style={{ fontFamily: fontNexus }}
                  >
                    <span className="flex items-center justify-between gap-2">
                      Compromisso da empresa e funcionamento do canal
                      <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
                    </span>
                  </summary>
                  <div
                    className="mt-4 space-y-4 border-t border-black/[0.06] pt-4 text-[0.95rem] leading-relaxed text-[rgba(17,29,48,0.78)]"
                    style={{ fontFamily: fontNexus }}
                  >
                    <p>
                      A <strong className="font-semibold text-[#111d30]">{form.client_name}</strong>{' '}
                      adota comportamento íntegro e transparente em suas relações internas e
                      externas, e abre espaço para que colaboradores e terceiros possam reportar
                      suspeitas de corrupção, fraude, práticas abusivas, discriminação, assédio ou
                      outras condutas contrárias aos nossos princípios.
                    </p>
                    <p className="mb-0">
                      Este canal é seguro e confidencial, permitindo que qualquer pessoa relate
                      irregularidades sem medo de represálias. As mensagens podem ser anônimas e
                      serão analisadas de maneira imparcial. Utilizamos medidas técnicas adequadas
                      para preservar a integridade e a confidencialidade das informações enviadas.
                    </p>
                  </div>
                </details>

                {landingFooter}
              </>
            ) : (
              <>
                <p
                  className="mb-8 text-center text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[rgba(17,29,48,0.45)]"
                  style={{ fontFamily: fontNexus }}
                >
                  Canal de denúncias · {form.client_name}
                </p>

                <CommitmentCopy clientName={form.client_name} variant="daterra" />

                <div className="mt-10">{trustStrip}</div>

                {primaryTaskBlock}

                <div className="mt-8 rounded-2xl border border-black/[0.06] bg-white/90 px-5 py-6 shadow-[0_8px_28px_rgba(0,0,0,0.04)]">
                  <p
                    className="text-[0.8rem] leading-relaxed text-[rgba(17,29,48,0.55)]"
                    style={{ fontFamily: fontNexus }}
                  >
                    Infraestrutura e canal mantidos por Finocchio e Ustra — Sociedade de Advogados,
                    em papel independente da gestão da empresa.
                  </p>
                </div>
              </>
            )}
          </div>

          {hubspotStyle ? <DaterraDarkFooter clientName={form.client_name} /> : null}
        </motion.section>
      </div>
    </>
  );
};

export default ComplaintPortalHero;
